// Map from cart contents to the richer model used for editing
// We may be restoring from an old kit that is no longer valid in the current catalog (for reordering old
// kits), so we only overwrite the default state with data we've validated against the catalog.
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { getFishAmplifierCatalog, PredefinedLabel, Scale } from "../automated-pro/RnaCatalog";
import { findOrganism, findOrganismByName } from "../KitToFormStateUtilityFunctions";
import { defaultAmpState, defaultBufferState, defaultProbeSetState } from "./EditKitForm";
import { numOfBindingSites } from "./KitDisplay";
import { ManualProPrice, RnaFishKitPrice, SimpleCatalogVariantProbeSet, SimpleLibraryOrganismCustomTargetProbeSet } from "./KitModel";
import { EditableOrganism, EditableTarget } from "./ProbeSetForm";
export var rnaFishKitToFormState = function (catalog, kit, admin, kitPrice) {
    var _a;
    var _b, _c;
    var probeSetEnabled = false;
    var probeSetState = defaultProbeSetState(catalog, kit.legacy, false);
    var ampEnabled = false;
    var ampState = defaultAmpState(getFishAmplifierCatalog(kit.legacy, catalog));
    // default to true so that when probe set is enabled when editing an existing kit it will start on catalog
    if (kit.probeSetComponent !== undefined) {
        probeSetEnabled = true;
        // todo temp channel state is ugly
        var tempAmpState = void 0;
        _a = probeKitToProbeFormState(probeSetState, kit.probeSetComponent, catalog, kit.legacy, ampState, kitPrice), probeSetState = _a[0], tempAmpState = _a[1];
        ampState = tempAmpState;
    }
    if (kit.amplifierComponent !== undefined) {
        var ampComponent = kit.amplifierComponent;
        ampEnabled = true;
        ampState = ampKitToAmpFormState(ampState, ampComponent, getFishAmplifierCatalog(kit.legacy, catalog), admin);
        if (!probeSetEnabled) {
            probeSetState = __assign(__assign({}, probeSetState), { hcrAmplifier: ampState.hcrAmplifier });
        }
        if ((kitPrice === null || kitPrice === void 0 ? void 0 : kitPrice.amplifierPriceCustom) === true) {
            ampState = __assign(__assign({}, ampState), { customPrice: {
                    listPriceCents: 0,
                    effectivePriceCents: kitPrice.amplifierPriceCents,
                } });
        }
    }
    var buffersEnabled = false;
    var bufferState = defaultBufferState(catalog, kit.legacy);
    if (kit.buffersComponent !== undefined) {
        var bufComp = kit.buffersComponent;
        buffersEnabled = true;
        if ((kitPrice === null || kitPrice === void 0 ? void 0 : kitPrice.buffersPriceCustom) === true) {
            bufferState = __assign(__assign({}, bufferState), { customPrice: {
                    listPriceCents: 0,
                    effectivePriceCents: kitPrice.buffersPriceCents,
                } });
        }
        bufferState = __assign(__assign({}, bufferState), { amp: bufComp.ampBuffer, hybridization: bufComp.hybridization, probeWash: bufComp.probeWash, goldProbeWash: bufComp.probeWash, ampWash: bufComp.ampWashBuffer });
    }
    var sampleTypeFormState = {
        sampleType: kit.legacy ? (_b = catalog.legacyFishBuffer.sampleTypes.find(function (t) { return t.id === kit.sampleType.id; })) !== null && _b !== void 0 ? _b : catalog.legacyFishBuffer.defaultSampleType : (_c = catalog.goldFishBuffer.sampleTypes.find(function (t) { return t.id === kit.sampleType.id; })) !== null && _c !== void 0 ? _c : catalog.goldFishBuffer.defaultSampleType
    };
    return {
        legacy: kit.legacy,
        ampEnabled: ampEnabled,
        ampState: ampState,
        buffersEnabled: buffersEnabled,
        bufferState: bufferState,
        sampleTypeFormState: sampleTypeFormState,
        comment: kit.comment,
        errors: [],
        price: undefined,
        priceLoading: false,
        probeSetEnabled: probeSetEnabled,
        probeSetState: probeSetState,
    };
};
export var probeKitToProbeFormState = function (probeSetState, probeSetComponent, catalog, legacy, ampState, kitPrice) {
    var probeSet = probeSetComponent.probeSet;
    var probeSetAmplifier;
    if (probeSet instanceof SimpleCatalogVariantProbeSet) {
        var foundScale = catalog.probeSet.catalog.fishScales.find(function (s) { return s.scaleValue === probeSetComponent.scale.scaleValue; });
        var scale = void 0;
        if (foundScale !== undefined) {
            scale = foundScale;
        }
        else {
            scale = new Scale(probeSetComponent.scale.scaleValue);
        }
        probeSetState = __assign(__assign({}, probeSetState), { scale: scale, probeLotNumber: kitPrice ? probeSet.probeLotNumber : undefined });
        var customerProvidedAmplifier = findMatchingAmplifierOrDefault(probeSet.hcrAmplifier, getFishAmplifierCatalog(legacy, catalog));
        var customerProvidedOrganism = findOrganismByName(extractNameBeforeParenthesis(probeSet.customerOrganismName), catalog);
        if (customerProvidedOrganism != null) {
            var customerProvidedTarget = customerProvidedOrganism.probeSetFamilies.find(function (psf) {
                return psf.geneSymbol.trim()
                    === extractNameBeforeParenthesis(probeSet.customerTargetName).trim();
            });
            if (customerProvidedTarget != null) {
                var customerProvidedVariant = customerProvidedTarget.variants.find(function (v) {
                    var accession = v.accession ? v.accession.transcriptAccession : "";
                    return v.targetMrnaSeq === probeSet.customerSequence
                        && v.probeSetSize === probeSet.customerProbeSetSize
                        && accession.trim().toLowerCase()
                            === probeSet.customerAccessionNum.trim().toLowerCase();
                });
                if (customerProvidedVariant != null) {
                    probeSetState = __assign(__assign({}, probeSetState), { organism: EditableOrganism.fromLibOrg(customerProvidedOrganism), target: EditableTarget.fromLibTarget(customerProvidedVariant, numOfBindingSites(customerProvidedVariant.probeSetSize)), targets: [
                            {
                                id: customerProvidedTarget.id,
                                geneSymbol: customerProvidedTarget.geneSymbol,
                                description: customerProvidedTarget.description,
                                synonyms: customerProvidedTarget.synonyms
                            }
                        ], hcrAmplifier: customerProvidedAmplifier });
                }
                else {
                    probeSetState = __assign(__assign({}, probeSetState), { organism: EditableOrganism.fromLibOrg(customerProvidedOrganism), target: new EditableTarget(customerProvidedTarget.geneSymbol, customerProvidedTarget.description, probeSet.customerAccessionNum, probeSet.customerSequence, numOfBindingSites(probeSet.customerProbeSetSize), customerProvidedTarget.id, undefined), targets: [
                            {
                                id: customerProvidedTarget.id,
                                geneSymbol: customerProvidedTarget.geneSymbol,
                                description: customerProvidedTarget.description,
                                synonyms: customerProvidedTarget.synonyms
                            }
                        ], hcrAmplifier: customerProvidedAmplifier });
                }
            }
            else {
                probeSetState = __assign(__assign({}, probeSetState), { organism: EditableOrganism.fromLibOrg(customerProvidedOrganism), target: new EditableTarget(probeSet.customerTargetName, "", probeSet.customerAccessionNum, probeSet.customerSequence, numOfBindingSites(probeSet.customerProbeSetSize), -1), hcrAmplifier: customerProvidedAmplifier });
            }
        }
        else {
            probeSetState = __assign(__assign({}, probeSetState), { organism: new EditableOrganism(probeSet.customerOrganismName, "", -1), target: new EditableTarget(probeSet.customerTargetName, "", probeSet.customerAccessionNum, probeSet.customerSequence, numOfBindingSites(probeSet.customerProbeSetSize), -1), hcrAmplifier: customerProvidedAmplifier });
        }
        var probeDesignOrganism = findOrganism(probeSet.organism, catalog);
        if (probeDesignOrganism !== undefined) {
            var probeDesignTarget = probeDesignOrganism.probeSetFamilies.find(function (psf) { return psf.id === probeSet.probeSetFamily.id; });
            if (probeDesignTarget !== undefined) {
                var variant = probeDesignTarget.variants.find(function (v) { return v.id === probeSet.variant.id; });
                if (variant !== undefined) {
                    probeSetState = __assign(__assign({}, probeSetState), { target: probeSetState.target.withProbeDesign(variant.id) });
                }
            }
        }
        probeSetAmplifier = customerProvidedAmplifier;
    }
    else if (probeSet instanceof SimpleLibraryOrganismCustomTargetProbeSet) {
        var libraryOrganism = findOrganism(probeSet.organism, catalog);
        var hcrAmplifier = findMatchingAmplifierOrDefault(probeSet.hcrAmplifier, getFishAmplifierCatalog(legacy, catalog));
        probeSetState = __assign(__assign({}, probeSetState), { organism: EditableOrganism.fromLibOrg(libraryOrganism), target: new EditableTarget(probeSet.target.name, "", probeSet.target.accession, probeSet.target.targetMrnaSeq, numOfBindingSites(probeSet.probeSetSize), -1), hcrAmplifier: hcrAmplifier, scale: probeSetComponent.scale, probeLotNumber: kitPrice ? probeSet.probeLotNumber : undefined });
        probeSetAmplifier = hcrAmplifier;
    }
    else {
        // SimpleOtherOrganismCustomTargetProbeSet
        var hcrAmplifier = findMatchingAmplifierOrDefault(probeSet.hcrAmplifier, getFishAmplifierCatalog(legacy, catalog));
        probeSetState = __assign(__assign({}, probeSetState), { organism: new EditableOrganism(probeSet.organism.name, "", -1), target: new EditableTarget(probeSet.target.name, "", probeSet.target.accession, probeSet.target.targetMrnaSeq, numOfBindingSites(probeSet.probeSetSize), -1), hcrAmplifier: hcrAmplifier, scale: probeSetComponent.scale, probeLotNumber: kitPrice ? probeSet.probeLotNumber : undefined });
        probeSetAmplifier = hcrAmplifier;
    }
    if (kitPrice instanceof RnaFishKitPrice) {
        if (kitPrice.probeSetPriceCustom) {
            probeSetState = __assign(__assign({}, probeSetState), { customPrice: {
                    listPriceCents: 0,
                    effectivePriceCents: kitPrice.probeSetPriceCents,
                } });
        }
    }
    else if (kitPrice instanceof ManualProPrice) {
        if (kitPrice.probeSetPriceCustom) {
            probeSetState = __assign(__assign({}, probeSetState), { customPrice: {
                    listPriceCents: 0,
                    effectivePriceCents: kitPrice.probeSetPriceCents
                } });
        }
    }
    if (probeSetAmplifier && ampState) {
        // even if though channel may be disabled at the moment, we want to preload its form state with the
        // channel from the probe set since the user can't change it
        ampState = __assign(__assign({}, ampState), { hcrAmplifier: probeSetAmplifier, label: getFishAmplifierCatalog(legacy, catalog).channels.find(function (c) { return c.hcrAmplifier.id === probeSetAmplifier.id; }).defaultLabel.label, scale: getFishAmplifierCatalog(legacy, catalog).channels.find(function (c) { return c.hcrAmplifier.id === probeSetAmplifier.id; }).defaultLabel.defaultScale });
    }
    return [probeSetState, ampState];
};
export var ampKitToAmpFormState = function (ampState, ampComponent, ampCatalog, admin) {
    // If channel was set above via the probe set, this should reset it to the same thing since the server
    // would have enforced that the amplifiers are the same.
    ampState = __assign(__assign({}, ampState), { hcrAmplifier: findMatchingAmplifierOrDefault(ampComponent.hcrAmplifier, ampCatalog) });
    // else, leave default channel in place
    var ampLabel = ampComponent.label;
    var foundChannel = ampCatalog.channels.find(function (a) {
        return a.hcrAmplifier.id === ampComponent.hcrAmplifier.id;
    });
    var label;
    var labelCatalog;
    var enableAdminSystems = false;
    if (ampLabel instanceof PredefinedLabel) {
        // Check if the label is part of the regular catalog
        labelCatalog = foundChannel === null || foundChannel === void 0 ? void 0 : foundChannel.labels.combined.find(function (l) {
            return l.label.id === ampLabel.id;
        });
        label = labelCatalog === null || labelCatalog === void 0 ? void 0 : labelCatalog.label;
        if (!label) {
            // When the label isn't a part of the regular catalog, it's an admin system label
            labelCatalog = foundChannel === null || foundChannel === void 0 ? void 0 : foundChannel.labels.admin.find(function (l) {
                return l.label.id === ampLabel.id;
            });
            label = labelCatalog === null || labelCatalog === void 0 ? void 0 : labelCatalog.label;
            if (labelCatalog && label) {
                enableAdminSystems = true;
            }
        }
    }
    else {
        // If the label is free text and this is an admin form state, we should enable admin systems, and we don't need to validate it
        if (admin) {
            label = ampLabel;
            enableAdminSystems = true;
        }
    }
    // If the label is not found in the catalog, use the default label
    if (label == null) {
        labelCatalog = ampCatalog.defaultChannel.defaultLabel;
        label = labelCatalog.label;
    }
    ampState = __assign(__assign({}, ampState), { adminSystems: enableAdminSystems, label: label });
    var scales = labelCatalog ? labelCatalog.scales : ampCatalog.freeTextLabelScales;
    // have to do the scale while doing the label because the valid scales depend on the label
    var foundScale = scales.find(function (s) { return s.valuePmol === ampComponent.scale.valuePmol; });
    if (foundScale !== undefined) {
        return __assign(__assign({}, ampState), { scale: foundScale });
    }
    else {
        return __assign(__assign({}, ampState), { scale: ampComponent.scale });
    }
};
export var findVolumeOrDefault = function (bm, query) {
    var found = bm.volumeMls.find(function (v) { return v.valueMl === query.valueMl; });
    if (found !== undefined) {
        return found;
    }
    return bm.defaultVolumeMl;
};
export var findMatchingAmplifierOrDefault = function (amp, catalog) {
    var _a;
    var found = (_a = catalog.channels.find(function (a) { return a.hcrAmplifier.id === amp.id; })) === null || _a === void 0 ? void 0 : _a.hcrAmplifier;
    if (found !== undefined) {
        return found;
    }
    return catalog.defaultChannel.hcrAmplifier;
};
export var extractNameBeforeParenthesis = function (name) {
    return name.split("(")[0].trim();
};
//# sourceMappingURL=RnaFishKitToFormState.js.map


//////////////////
// WEBPACK FOOTER
// ../common/build/dist/kit/rna-fish/RnaFishKitToFormState.js
// module id = 53
// module chunks = 0